'use client'
import React, { FC, PropsWithChildren } from 'react'
import OutsideClickHandler from 'react-outside-click-handler'
import { isHTMLElement } from '@floating-ui/utils/dom'
import { timeFormat } from '@/root/utils/timeFormat'
import { Direction, SearchBarDate } from '@/root/shared-types'
import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { checkIsDayDisabled } from '@/root/shared-helpers'
import { ChevronUp, DayPickerTitle, DatePlaceholder } from '@/root/modules/tuuleliinid'
import DayPicker from '@/root/components/shared/DayPicker/DayPicker'
import { Checkbox } from '@/root/ui/tuuleliinid/Checkbox/Checkbox'
import cn from 'classnames'

interface SearchbarDayPickersProps extends PropsWithChildren {
  selectedDate: SearchBarDate
  onDateChange: (direction: Direction) => (date: Date) => void
  availableDates: { availableDatesTo: Array<Date>; availableDatesFrom: Array<Date> }
  titleTo: string
  isOpened: boolean
  toggleDayPickers(): void
  titleFrom?: string
  isRoundTrip?: boolean
  isRoundTripChecked?: boolean
  onRoundTripCheck?(): void
  isRoundTripDisabled?: boolean
  roundTripLabel?: string
}

export const SearchbarDayPickers: FC<SearchbarDayPickersProps> = ({
  selectedDate,
  onDateChange,
  availableDates,
  titleTo,
  titleFrom,
  isRoundTrip = false,
  isOpened,
  toggleDayPickers,
  children,
  isRoundTripChecked = false,
  onRoundTripCheck,
  isRoundTripDisabled = false,
  roundTripLabel,
}) => {
  const { getTranslation } = useGetTranslations()
  const { locale } = useCustomRouter()

  const outboundDayPickerClassName = cn('w-full p-2 xxs:p-4', {
    'xxs:pb-2 lg:w-93 lg:pe-2 lg:pb-4': isRoundTrip,
  })
  const mobilePopupClassName =
    'absolute -top-[20rem] left-[calc(50%-0.625rem)] z-10 flex w-[92vw] max-w-[26rem] -translate-x-1/2 flex-col items-center overflow-hidden bg-white shadow-lg lg:hidden'
  const desktopPopupClassName = cn(
    'hidden lg:absolute lg:top-full lg:left-1/2 lg:z-10 lg:mt-2 lg:flex lg:-translate-x-1/2 lg:flex-row lg:items-end lg:overflow-hidden lg:bg-white lg:shadow-lg',
    {
      'lg:max-w-[48rem]': isRoundTrip,
    }
  )

  const selectedDateTo = selectedDate.to && timeFormat(selectedDate.to, locale)
  const selectedDateFrom = selectedDate.from && timeFormat(selectedDate.from, locale)

  const outboundCalendar = (
    <DayPicker
      className={outboundDayPickerClassName}
      isDayDisabled={checkIsDayDisabled({
        availableDates: availableDates.availableDatesTo,
      })}
      onDayClick={onDateChange('to')}
      selectedDate={selectedDate.to}
    >
      {titleTo && <DayPickerTitle title={titleTo} />}
    </DayPicker>
  )
  const returnCalendar = isRoundTrip ? (
    <DayPicker
      className="xxs:w-95 xxs:p-4 xxs:pt-2 w-full p-2 lg:w-93 lg:ps-2 lg:pt-4"
      defaultMonth={selectedDate.from || selectedDate.to}
      isDayDisabled={checkIsDayDisabled({
        availableDates: availableDates.availableDatesFrom,
        disableBeforeDate: selectedDate.to,
      })}
      onDayClick={onDateChange('from')}
      selectedDate={selectedDate.from}
    >
      {titleFrom && <DayPickerTitle title={titleFrom} />}
    </DayPicker>
  ) : null
  const desktopCalendarContent = (
    <>
      {outboundCalendar}
      {isRoundTrip && (
        <>
          <ChevronUp onClick={toggleDayPickers} />
          {returnCalendar}
        </>
      )}
    </>
  )
  const mobileRoundTripControl =
    roundTripLabel && onRoundTripCheck ? (
      <div className="border-neutral-normal-lighter w-full border-b px-4 py-3">
        <Checkbox
          checked={isRoundTripChecked}
          className="items-center text-base font-bold uppercase"
          disabled={isRoundTripDisabled}
          label={roundTripLabel}
          onChange={onRoundTripCheck}
          variant="success"
        />
      </div>
    ) : null

  return (
    <div className="relative flex w-full min-w-0 flex-col items-start">
      <button className="text-primary-base flex gap-1 text-start" onClick={toggleDayPickers} type="button">
        <DatePlaceholder
          date={selectedDateTo}
          placeholder={getTranslation('searchBarWhenPlaceholder')}
          truncate={isRoundTrip}
        />
        {isRoundTrip && (
          <>
            <DatePlaceholder placeholder={''} displaySeparator />
            <DatePlaceholder
              placeholder={getTranslation('searchBarWhenPlaceholder')}
              date={selectedDateFrom}
              truncate
            />
          </>
        )}
      </button>
      {isOpened && (
        <OutsideClickHandler
          onOutsideClick={(e) => {
            if (!(isHTMLElement(e.target) && e.target.closest('label')?.querySelector('input[type="checkbox"]'))) {
              toggleDayPickers()
            }
          }}
        >
          <>
            <div className={mobilePopupClassName} data-testid="searchbar-day-pickers-popup-mobile">
              {outboundCalendar}
              {mobileRoundTripControl}
              {returnCalendar}
            </div>
            <div className={desktopPopupClassName} data-testid="searchbar-day-pickers-popup-desktop">
              {desktopCalendarContent}
            </div>
          </>
        </OutsideClickHandler>
      )}
      {children}
    </div>
  )
}
